import React, { useContext } from 'react'
import PropTypes from 'prop-types'
import { Image } from '@sainsburys-tech/bolt'
import { agnostify } from '@sainsburys-tech/boltui-utils'
import { ThemeContext } from 'styled-components'
import { createGolDataAttributes, joinLinkTitles } from '../../../../helpers/tracking'
import M032 from '../../M032/M032'
import {
  StyledContainer,
  StyledHeading,
  StyledImageWrapper,
  StyledCopy,
  StyledGridWrapper,
  StyledGrid
} from './M0015.styles'

/**
 * - Flexible Hero header primarily for homepages.
 * - The component can also be used on feature/event/inspirational pages
 *  where there is need to promote at the top of the page to take the user to the content/promotion.
 */
const dataTest = 'm0015'
const M0015 = ({
  alignment,
  bodyCopy,
  buttonColor,
  heading,
  isSecondaryHeading,
  images,
  imageAltText,
  kind,
  links,
  linkTarget,
  mediaRatio,
  onLinkClick,
  primaryLink
}) => {
  // If the images array is undefined, empty or doesn't have a desktop image source, return nothing.
  // In reality, this will never happen, due to Magnolia restrictions
  if (!(images && images.length > 0 && images[0] && typeof images[0].source === 'string')) return null
  const [{ source: imageUrl }, { source: imageMobileUrl } = {}] = images

  // Fetch the styled-components theme from context
  const theme = useContext(ThemeContext)
  // Get the breakpoint for switching from four to two columns, reducing the typography of the header and
  // showing the portrait hero image.
  const breakpointWidth = theme.size.breakpoint['max-width'].xs
  // If an image URL is provided for smaller viewports, form an array of additional media.
  // Omit if not provided, and the image will fallback to using the main image for all viewports.
  const additionalMedia = imageMobileUrl
    ? [{ src: imageMobileUrl, query: `(max-width: ${breakpointWidth})`, width: breakpointWidth }]
    : undefined

  const getGOLDataAttributes = (linkTitle, url, action) => {
    return createGolDataAttributes(
      'isolated',
      'M0015 - NA',
      action === 'Load' ? joinLinkTitles(links, [primaryLink?.linkTitle]) : linkTitle,
      url,
      heading,
      action
    )
  }

  const addGolTracking = (evt, linkTitle, linkUrl) => {
    const dataAttributes = getGOLDataAttributes(linkTitle, linkUrl)
    Object.keys(dataAttributes).forEach((attribute) =>
      evt.currentTarget.setAttribute(attribute, dataAttributes[attribute])
    )
  }

  const ctaClick = (event, linkUrl, linkTitle) => {
    if (onLinkClick) {
      onLinkClick(event, agnostify(linkUrl))
    }
    addGolTracking(event, linkTitle, linkUrl)
  }

  return (
    <StyledContainer data-test={`${dataTest}-container`} {...getGOLDataAttributes('', primaryLink?.linkUrl, 'Load')}>
      {heading && (
        <StyledHeading as={isSecondaryHeading ? 'h2' : 'h1'} data-test={`${dataTest}-heading`} alignment={alignment}>
          {heading}
        </StyledHeading>
      )}

      <StyledImageWrapper
        data-test={`${dataTest}-image-wrapper`}
        as={primaryLink ? 'a' : undefined}
        href={primaryLink ? primaryLink.linkUrl : undefined}
        title={primaryLink ? primaryLink?.linkTitle : undefined}
        breakpoint={breakpointWidth}
        onClick={(evt) => (primaryLink ? ctaClick(evt, primaryLink.linkUrl, primaryLink.linkTitle) : undefined)}
        mediaRatio={mediaRatio}
        target={linkTarget}
        tabIndex={primaryLink && 0}
      >
        <Image src={imageUrl} additionalMedia={additionalMedia} alt={imageAltText} responsive />
      </StyledImageWrapper>
      {bodyCopy && (
        <StyledCopy data-test={`${dataTest}-body-copy`} alignment={alignment}>
          {bodyCopy}
        </StyledCopy>
      )}
      <StyledGridWrapper>
        <StyledGrid data-test={`${dataTest}-grid-container`} breakpoint={breakpointWidth} alignment={alignment}>
          {links &&
            links.map((link, index) => (
              <li data-test={`${dataTest}-cta-container-${index}`} key={link.linkTitle}>
                <M032
                  backgroundColor={buttonColor}
                  ctaCopy={link.linkName}
                  linkTitle={link.linkTitle}
                  linkUrl={link.linkUrl}
                  onLinkClick={(evt) => ctaClick(evt, link.linkUrl, link.linkTitle)}
                  kind={kind}
                  linkTarget={linkTarget}
                  full
                />
              </li>
            ))}
        </StyledGrid>
      </StyledGridWrapper>
    </StyledContainer>
  )
}

M0015.propTypes = {
  /** Array of CTA items to be shown in hero */
  links: PropTypes.arrayOf(
    PropTypes.shape({
      /** CTA item name */
      linkName: PropTypes.string.isRequired,
      /** CTA item URL */
      linkUrl: PropTypes.string.isRequired,
      /** CTA item title */
      linkTitle: PropTypes.string.isRequired
    })
  ),
  /** Option of opening in a new tab, defaults to an empty string  */
  linkTarget: PropTypes.oneOf(['', '_blank', '_self']),
  /** Optional button colour */
  buttonColor: PropTypes.string,
  /**
   * Array of images be used for hero.
   * The first element will be used for desktop.
   * The second element, if present, will be used for mobile.
   *  */
  images: PropTypes.arrayOf(
    PropTypes.shape({
      /** URL for the image */
      source: PropTypes.string.isRequired
    })
  ).isRequired,
  primaryLink: PropTypes.shape({
    /** Hero CTA URL */
    linkUrl: PropTypes.string.isRequired,
    /** Hero CTA title */
    linkTitle: PropTypes.string.isRequired
  }),
  /** The M032 "kind" that will be used for CTAs, either "primary" or "secondary" */
  kind: PropTypes.oneOf(['primary', 'secondary']),
  /** Alt text for the hero image */
  imageAltText: PropTypes.string.isRequired,
  /** Aspect ratio to be used for imagery, either "short" or "tall" */
  mediaRatio: PropTypes.oneOf(['short', 'tall']),
  /** Optional heading text */
  heading: PropTypes.string,
  /** Optional boolean to render an h2 tag instead of h1 for heading */
  isSecondaryHeading: PropTypes.bool,
  /** Text to be showing in the body */
  bodyCopy: PropTypes.string,
  /** Function to be called when hero or CTAs are clicked */
  onLinkClick: PropTypes.func,
  /** Alignment for the text and buttons */
  alignment: PropTypes.string
}

M0015.defaultProps = {
  alignment: 'center',
  bodyCopy: '',
  buttonColor: '',
  heading: '',
  isSecondaryHeading: false,
  kind: 'primary',
  linkTarget: '',
  mediaRatio: 'tall',
  onLinkClick: null
}

export default M0015
